import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaQuestionCircle, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaSearch, FaFileAlt, FaGavel, FaBriefcaseMedical, FaFileSignature } from "react-icons/fa";
import "./ResourcesShared.css";

const CompanyName1 = () => {
    const navigate = useNavigate();

    const relatedArticles = [
        {
            title: "How to Register a Business Name",
            description: "Step-by-step guide to registering your business name with the state.",
            link: "/resources/register-business-name"
        },
        {
            title: "Trademark vs. Business Name",
            description: "Understanding the difference between a registered business name and a trademark.",
            link: "/resources/trademark-vs-business-name"
        },
        {
            title: "DBA (Doing Business As) Guide",
            description: "Learn when and how to file a DBA for your business.",
            link: "/resources/dba-guide"
        }
    ];

    return (
        <div className="rs-container">
            <main className="rs-main-content">
                <div className="rs-container-1200">
                    <nav className="rs-breadcrumb">
                        <ol>
                            <li><a href="/help">Help Center</a></li>
                            <li><a href="/resources">Resources</a></li>
                            <li>Company Name</li>
                        </ol>
                    </nav>

                    <article className="rs-article">
                        <header className="rs-article-header">
                            <h1 className="rs-article-title">Restricted Business Names in the U.S.: What You Need to Know</h1>
                            <div className="rs-article-meta">
                                <span>Posted by BooksComply Team</span>
                                <span>•</span>
                                <span>Last updated August 2025</span>
                            </div>
                        </header>
                        
                        <div className="rs-article-content">
                            <div className="rs-intro-card">
                                <FaExclamationTriangle className="rs-intro-icon" />
                                <p>
                                    Choosing a business name is a crucial step when incorporating a company in the U.S. However, certain words and phrases are restricted or prohibited by state and federal regulations. These restrictions help prevent fraud, misleading business practices, and unauthorized use of regulated terms.
                                </p>
                            </div>

                            <section className="rs-section">
                                <h2>What Are Restricted Business Names?</h2>
                                <p>
                                    Restricted business names are those that require special approval, licensing, or compliance before they can be used. Each U.S. state has its own rules, but some general restrictions apply nationwide.
                                </p>
                            </section>

                            <section className="rs-section">
                                <h2><FaGavel className="rs-feature-icon" /> 1. Names That Imply Government Affiliation</h2>
                                <p>
                                    You cannot use words that suggest an association with U.S. government agencies, including:
                                </p>
                                <ul>
                                    <li>FBI</li>
                                    <li>CIA</li>
                                    <li>Treasury</li>
                                    <li>Federal Reserve</li>
                                    <li>State Department</li>
                                </ul>
                                <div className="rs-note">
                                    <p><strong>Why?</strong> These names can mislead the public into thinking your company is part of the government.</p>
                                </div>
                            </section>

                            <section className="rs-section">
                                <h2><FaFileSignature className="rs-feature-icon" /> 2. Names That Require Special Licenses</h2>
                                <p>
                                    Certain professions and industries require state licensing. You cannot use these terms unless properly licensed:
                                </p>
                                <ul>
                                    <li>Bank, Banker, Banking</li>
                                    <li>Attorney, Law Firm</li>
                                    <li>University, College, School</li>
                                    <li>Insurance, Assurance, Fidelity</li>
                                    <li>Trust, Trustee</li>
                                </ul>
                                <div className="rs-warning">
                                    <p><strong>Note:</strong> Some states allow these terms with proper documentation proving licensure.</p>
                                </div>
                            </section>

                            <section className="rs-section">
                                <h2><FaExclamationTriangle className="rs-feature-icon" /> 3. Names That Are Deceptive or Misleading</h2>
                                <p>Your business name cannot mislead customers about what you do.</p>
                                <div className="rs-note">
                                    <p><strong>Examples:</strong> "U.S. National Lottery, Inc." (There is no national lottery) or "Federal Grants Agency" (Implying a government function)</p>
                                </div>
                            </section>

                            <section className="rs-section">
                                <h2><FaSearch className="rs-feature-icon" /> 4. Names That Resemble Existing Businesses</h2>
                                <p>Each U.S. state prohibits business names that are too similar to an existing registered company.</p>
                                <h3>How to Check for Name Availability?</h3>
                                <ul>
                                    <li>Search the Secretary of State's business name database</li>
                                    <li>Check trademark availability on the USPTO website</li>
                                    <li>Consider reserving your name if you're not ready to register yet</li>
                                </ul>
                            </section>

                            <section className="rs-section">
                                <h2><FaTimesCircle className="rs-feature-icon" /> 5. Profane, Offensive, or Inappropriate Words</h2>
                                <p>Names that include obscene, offensive, or discriminatory language will be rejected.</p>
                                <div className="rs-note">
                                    <p><strong>Example:</strong> Hate speech, racial slurs, or explicit words cannot be used in business names.</p>
                                </div>
                            </section>

                            <section className="rs-section">
                                <h2><FaQuestionCircle className="rs-feature-icon" /> How to Register a Restricted Business Name?</h2>
                                <p>If your desired name falls under a restricted category, you may still be able to use it by:</p>
                                <ul>
                                    <li><strong>Providing Licensing Proof</strong> – For regulated professions, submit your license or certification.</li>
                                    <li><strong>Getting State Approval</strong> – Some states allow exemptions if the name accurately reflects your business.</li>
                                    <li><strong>Adding Modifiers</strong> – Adjusting the name slightly to avoid confusion with existing businesses.</li>
                                </ul>
                            </section>

                        </div>
                    </article>
                </div>
            </main>
        </div>
    );
};

export default CompanyName1;